/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
/* eslint-disable @atlassian/relay/unused-fields */
import { useCallback } from 'react';
import isNil from 'lodash/isNil';
import { fetchQuery, graphql } from 'react-relay';
import { createOperationDescriptor } from 'relay-runtime';
import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import getRelayEnvironment from '@atlassian/jira-relay-environment';
import useFetchReleasesJiraBusinessCalendarQuery, {
	type useFetchReleasesJiraBusinessCalendarQuery as useFetchReleasesJiraBusinessCalendarQueryType,
} from '@atlassian/jira-relay/src/__generated__/useFetchReleasesJiraBusinessCalendarQuery.graphql';
import type {
	FetchReleasesInputType,
	FetchReleasesResponse,
	UseFetchReleasesJiraBusinessCalendarQueryResponse,
} from './types';

export const useFetchReleases = () => {
	const fetchReleases = useCallback(
		async ({
			projectAris,
			releaseDateAfter,
			releaseDateBefore,
			first = 50,
		}: FetchReleasesInputType): FetchReleasesResponse => {
			if (projectAris?.length === 0) {
				return [];
			}

			const query = graphql`
				query useFetchReleasesJiraBusinessCalendarQuery(
					$projectAris: [ID!]!
					$releaseDateAfter: Date
					$releaseDateBefore: Date
					$first: Int
				) {
					jira {
						versionsForProjects(
							jiraProjectIds: $projectAris
							filter: [RELEASED, UNRELEASED]
							first: $first
							releaseDateAfter: $releaseDateAfter
							releaseDateBefore: $releaseDateBefore
						) @optIn(to: "JiraVersionsForProjects") {
							edges {
								cursor
								node {
									id @required(action: NONE)
									versionId @required(action: NONE)
									name @required(action: NONE)
									iconUrl
									status @required(action: NONE)
									description
									startDate
									releaseDate @required(action: NONE)
									project @required(action: NONE) {
										key @required(action: NONE)
										name @required(action: NONE)
										avatar @required(action: NONE) {
											large @required(action: NONE)
											small
											__typename
										}
									}
									doneIssues: issues(filters: { statusCategories: DONE }) @required(action: NONE) {
										totalCount @required(action: NONE)
									}
									inprogressIssues: issues(filters: { statusCategories: IN_PROGRESS })
										@required(action: NONE) {
										totalCount @required(action: NONE)
									}
									todoIssues: issues(filters: { statusCategories: TODO }) @required(action: NONE) {
										totalCount @required(action: NONE)
									}
									...calendarReleaseForMultipleProjects_businessCalendar
								}
							}
							pageInfo {
								endCursor
								hasNextPage
							}
						}
					}
				}
			`;
			const variables = {
				projectAris,
				releaseDateAfter: releaseDateBefore.toISOString(),
				releaseDateBefore: releaseDateAfter.toISOString(),
				first,
			};

			try {
				const request = fetchQuery<useFetchReleasesJiraBusinessCalendarQueryType>(
					getRelayEnvironment(),
					query,
					variables,
					{ fetchPolicy: 'store-or-network' },
				);
				/**
				 * fetchQuery will NOT retain the data for the query, meaning that it is not guaranteed that the
				 * data will remain saved in the Relay store at any point after the request completes. We need to
				 * explicitly retain the operation to ensure it doesn't get deleted.
				 * See https://relay.dev/docs/api-reference/fetch-query/#behavior
				 * */
				getRelayEnvironment().retain(
					createOperationDescriptor(useFetchReleasesJiraBusinessCalendarQuery, variables),
				);
				const releasesResponse: UseFetchReleasesJiraBusinessCalendarQueryResponse =
					await request.toPromise();
				const errors = releasesResponse?.errors;
				if (errors != null) {
					throw new GraphQLErrors(errors);
				}

				if (isNil(releasesResponse?.jira?.versionsForProjects?.edges)) {
					return [];
				}
				return releasesResponse.jira.versionsForProjects.edges.map((edge) => edge?.node);

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useFetchReleasesJiraBusinessCalendarQuery',
						packageName: 'jiraSharedSoftwareDates',
						teamName: 'a4t-werewolves',
					},
					error,
				});
				throw error;
			}
		},
		[],
	);

	return { fetchReleases } as const;
};
